import React, { FC, useRef } from 'react';
import { alpha, Box, Button } from '@mui/material';
import styles from './FileUploadComponent.module.scss';
import SnackbarUtils from '../../snackbar-utils';

interface FileUploadComponentProps {
    fileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onHover?: (hasHover: boolean) => void,
    disabled?: boolean;
    accept?: string[];
    maxFileSize?: number | undefined;
    hasFile?: boolean;
    isButton?: boolean;
    multiple?: boolean;
    buttonVariant?: 'outlined' | 'text' | 'contained' | undefined;
    noStyles?: boolean;
    triggerWrapper?: UploadTrigger
    children: React.ReactNode;
}

export const triggerTemplate: UploadTrigger = { trigger: () => null };

interface UploadTrigger {
    trigger: () => void
}

const FileUploadComponent: FC<FileUploadComponentProps> = (props) => {
    const {
        fileChange,
        onHover,
        hasFile,
        maxFileSize,
        accept,
        disabled,
        children,
        isButton,
        buttonVariant,
        multiple,
        noStyles,
        triggerWrapper,
    } = props;

    const onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void = (e) => {
        const files = e.target.files ?? [];
        let fileTypesValid = true;
        let fileSizesValid = true;
        const validationMessages = [];
        for (let f = 0; f < files.length; f++) {
            if (accept && !accept.some((a) => files[f].name.endsWith(a))) {
                fileTypesValid = false;
                validationMessages.push(`${files[f].name} file type not supported`);
            }
            if (maxFileSize && files[f].size > maxFileSize) {
                fileSizesValid = false;
                validationMessages.push(`${files[f].name} is to large`);
            }
        }
        if (fileTypesValid && fileSizesValid) {
            fileChange(e);
        } else if (validationMessages.length > 0) {
            const msg = validationMessages.join('\r\n');
            SnackbarUtils.error(msg);
        } else {
            SnackbarUtils.error('Invalid file');
        }
        e.target.value = '';
    };

    const buttonFileInput = useRef<HTMLInputElement>(null);

    const handleButtonClick = () => {
        if (buttonFileInput != null && buttonFileInput.current != null) {
            buttonFileInput!.current!.click();
        }
    };

    if (triggerWrapper) {
        triggerWrapper.trigger = handleButtonClick;
    }

    return (
        <span>
            {/* TODO: remove isButton if the noStyles can be used for all the same purposes */}
            {/* eslint-disable-next-line no-nested-ternary  */}
            {noStyles && triggerWrapper ? (
                <Box
                    component="label"
                    htmlFor="upload-input"
                    onMouseEnter={() => onHover && onHover(true)}
                    onMouseLeave={() => onHover && onHover(false)}
                >
                    <input
                        style={{ display: 'none' }}
                        disabled={disabled}
                        accept={accept?.join(',') ?? undefined}
                        className={styles.input}
                        id="upload-input"
                        onChange={(e) => onFileChange(e)}
                        type="file"
                        multiple={!!multiple}
                        ref={buttonFileInput}
                    />
                    {children}
                </Box>
            ) : isButton ? (
                <Button
                    onClick={() => handleButtonClick()}
                    variant={buttonVariant}
                >
                    <input
                        style={{ display: 'none' }}
                        disabled={disabled}
                        accept={accept?.join(',') ?? undefined}
                        className={styles.input}
                        id="upload-input"
                        onChange={(e) => onFileChange(e)}
                        type="file"
                        ref={buttonFileInput}
                        multiple={!!multiple}
                    />
                    {children}
                </Button>

            ) : (
                <div
                    className={styles.FileUploadComponent}
                    data-testid="FileUploadComponent"
                    style={{ display: 'flex', flex: 1 }}
                >
                    <Box
                        component="label"
                        htmlFor="upload-input"
                        onMouseEnter={() => onHover && onHover(true)}
                        onMouseLeave={() => onHover && onHover(false)}
                        sx={{
                            display: 'flex',
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            outline: 1,
                            borderRadius: '4px',
                            margin: '1px',
                            outlineStyle: 'dashed',
                            outlineColor: (t) => t.palette.primary.main,
                            ...(!hasFile && {
                                background: (t) => alpha(t.palette.primary.main, 0.07),
                            }),
                            ...(!disabled && !!onHover && {
                                '&:hover': {
                                    borderRadius: '4px',
                                    outline: 2,
                                    outlineStyle: 'solid',
                                    boxSizing: 'border-box',
                                    ...(!hasFile && {
                                        outlineColor: (t) => t.palette.primary.main,
                                    }),
                                    ...(hasFile && {
                                        outlineColor: (t) => t.palette.grey500.main,
                                    }),
                                },
                            }),
                            ...(hasFile && {
                                outlineColor: (t) => t.palette.grey500.main,
                                outlineStyle: 'solid',
                            }),
                        }}
                    >
                        {children}
                        <input
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                opacity: 0,
                                width: '100%',
                                height: '100%',
                                display: 'block',
                            }}
                            disabled={disabled}
                            accept={accept?.join(',') ?? undefined}
                            className={styles.input}
                            id="upload-input"
                            onChange={(e) => onFileChange(e)}
                            type="file"
                        />
                    </Box>
                </div>
            )}
        </span>

    );
};

FileUploadComponent.defaultProps = {
    disabled: false,
    onHover: () => null,
    accept: ['.jpg', '.png'],
    maxFileSize: undefined,
    hasFile: false,
    isButton: false,
    buttonVariant: 'contained',
    multiple: false,
    noStyles: false,
    triggerWrapper: undefined,
};

export default FileUploadComponent;
